import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import InterestsIcon from "@mui/icons-material/Interests";
import LayersIcon from "@mui/icons-material/Layers";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import {
  DialogActions,
  DialogContent,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Autocomplete,
  Button,
  DatePicker,
  Field,
  FieldsContainer,
  Form,
  FormSectionTitle,
  PageHeader,
  TextField,
} from "common";
import List from "common/components/Inputs/List/List";
import PriceInputBase from "components/PriceInputBase";
import {
  ScheduleLimit,
  ScheduleOption,
  ScheduleSeatingLayout,
  ScheduleVariant,
} from "features/schedules/scheduleApi";
import { LIMIT_TYPES } from "features/schedules/scheduleConstants";
import I18n from "i18n";
import { GroupEventDefaultsButton } from "./GroupEventDefaultsButton";
import useGroupEventCreateForm, {
  GroupEventDay,
} from "./useGroupEventCreateForm";

interface Props {
  onClose: () => void;
}

function CopyTimesButton({ onChange, disabled }: any) {
  return (
    <Tooltip title={I18n.t("Event.groupCreate.copyTimesTooltip")}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => onChange?.({ target: { value: true } })}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
CopyTimesButton.displayName = "CopyTimesButton";

export default function GroupEventCreateForm({ onClose }: Props) {
  const {
    item,
    errors,
    onChange,
    save,
    saving,
    getRequiredFields,
    formOptions,
    programs,
    hasOptions,
    times,
    type,
    reset,
  } = useGroupEventCreateForm(onClose);

  function handleClose() {
    onClose();
    reset();
  }

  return (
    <>
      <PageHeader
        avatar={<CalendarMonthIcon />}
        title={I18n.t("Event.groupCreate.dialogTitle")}
        onClose={handleClose}
        loading={saving}
      >
        <GroupEventDefaultsButton
          disabled={!item.boat_id || !item.program_ids?.length}
          variant="outlined"
          onUpdate={values => onChange(values)}
          program_ids={item.program_ids}
          boat_id={item.boat_id as number}
        />
      </PageHeader>
      <DialogContent>
        <Form
          item={item}
          errors={errors}
          onChange={onChange}
          translationCategory="Event.attributes"
          requiredFields={getRequiredFields()}
          options={formOptions}
        >
          <FieldsContainer>
            <Field fullWidth>
              <Paper sx={{ p: 2, mb: 2 }}>
                <FieldsContainer>
                  <FormSectionTitle
                    titleProps={{ variant: "h6" }}
                    icon={<DirectionsBoatIcon />}
                    title="Hajó indulás"
                    sx={{ mb: 2, gap: 2 }}
                  />
                  <Field>
                    <Autocomplete id="boat_id" />
                  </Field>
                  <Field>
                    <TextField id="duration" inputProps={{ type: "number" }} />
                  </Field>
                  <Field>
                    <TextField
                      id="number_of_places"
                      inputProps={{ type: "number" }}
                    />
                  </Field>
                  <Field>
                    <TextField
                      id="number_of_online_places"
                      inputProps={{ type: "number" }}
                    />
                  </Field>
                  <Field>
                    <TextField
                      id="places_tolerance"
                      inputProps={{ type: "number" }}
                    />
                  </Field>
                </FieldsContainer>
              </Paper>
            </Field>
            <Field fullWidth>
              <Autocomplete id="program_ids" multiple colorAttribute="color" />
            </Field>
            <Field fullWidth>
              <List<ScheduleVariant>
                id="variants"
                label={I18n.t("Price.title")}
                icon={<InterestsIcon />}
                columns={[
                  {
                    id: "variant_id",
                    title: I18n.t("Price.attributes.variant_id"),
                    inputComponent: Autocomplete,
                    inputProps: {
                      options: formOptions.variants,
                      colorAttribute: "program.color",
                    },
                    headerProps: { width: "50%" },
                  },
                  {
                    id: "adult_price",
                    title: I18n.t("Price.attributes.adult_price"),
                    inputComponent: PriceInputBase,
                    inputProps: { type: "number", switchDisabled: true },
                  },
                  {
                    id: "kid_price",
                    title: I18n.t("Price.attributes.kid_price"),
                    inputComponent: PriceInputBase,
                    inputProps: { type: "number" },
                  },
                ]}
              />
            </Field>
            <Field fullWidth>
              <List<ScheduleLimit>
                id="limits"
                label={I18n.t("Limit.title")}
                icon={<ProductionQuantityLimitsIcon />}
                onRowCahnge={(limitItem, _index, field, value) => {
                  if (field === "type") {
                    if (value === LIMIT_TYPES.PROGRAM) {
                      const variant_ids = formOptions.variants
                        .filter(
                          (v: any) => v.program_id === limitItem.program_id,
                        )
                        .map((v: any) => v.id);
                      return { ...limitItem, variant_ids, name: undefined };
                    }
                    return { ...limitItem, program_id: undefined };
                  }
                  if (field === "program_id") {
                    const variant_ids = formOptions.variants
                      .filter((v: any) => v.program_id === value)
                      .map((v: any) => v.id);
                    return { ...limitItem, variant_ids, name: undefined };
                  }
                  return limitItem;
                }}
                columns={[
                  {
                    id: "type",
                    title: I18n.t("Limit.attributes.type"),
                    inputComponent: Autocomplete,
                    inputProps: {
                      options: type.filter((o: any) => o.value !== "boat"),
                    },
                    headerProps: { width: "140px" },
                  },
                  (limitItem, _index) => {
                    if (limitItem?.type === LIMIT_TYPES.PROGRAM) {
                      return {
                        id: "program_id",
                        title: I18n.t("Limit.attributes.name"),
                        inputComponent: Autocomplete,
                        inputProps: {
                          options: programs,
                          colorAttribute: "color",
                        },
                        headerProps: { width: "50%" },
                      };
                    }
                    return {
                      id: "name",
                      title: I18n.t("Limit.attributes.name"),
                      inputComponent: InputBase,
                      inputProps: {},
                      headerProps: { width: "140px" },
                    };
                  },
                  {
                    id: "variant_ids",
                    title: I18n.t("Limit.attributes.variant_ids"),
                    inputComponent: Autocomplete,
                    inputProps: (limitItem?: ScheduleLimit) => ({
                      disabled: limitItem?.type === LIMIT_TYPES.PROGRAM,
                      multiple: true,
                      options: formOptions.variants,
                      colorAttribute: "program.color",
                    }),
                    headerProps: { width: "50%" },
                  },
                  {
                    id: "number_of_places",
                    title: I18n.t("Limit.attributes.number_of_places"),
                    inputComponent: InputBase,
                    inputProps: { type: "number" },
                  },
                  {
                    id: "number_of_online_places",
                    title: I18n.t("Limit.attributes.number_of_online_places"),
                    inputComponent: InputBase,
                    inputProps: { type: "number" },
                  },
                  {
                    id: "places_tolerance",
                    title: I18n.t("Limit.attributes.places_tolerance"),
                    inputComponent: InputBase,
                    inputProps: { type: "number" },
                  },
                ]}
              />
            </Field>
            {hasOptions && (
              <Field fullWidth>
                <List<ScheduleOption>
                  id="options"
                  label={I18n.t("Event.optionsTitle")}
                  icon={<WorkspacePremiumIcon />}
                  columns={[
                    {
                      id: "option_id",
                      title: I18n.t("Event.groupCreate.optionIdColumn"),
                      inputComponent: Autocomplete,
                      inputProps: {
                        options: formOptions.options,
                        colorAttribute: "program.color",
                      },
                      headerProps: { width: "50%" },
                    },
                    {
                      id: "price",
                      title: I18n.t("Price.attributes.price"),
                      inputComponent: PriceInputBase,
                      inputProps: { type: "number", switchDisabled: true },
                    },
                    {
                      id: "limit",
                      title: I18n.t("Price.attributes.limit"),
                      inputComponent: InputBase,
                      inputProps: { type: "number" },
                    },
                  ]}
                />
              </Field>
            )}
            <Field fullWidth>
              <List<ScheduleSeatingLayout>
                id="seating_layouts"
                label={I18n.t("Event.groupCreate.seatingLayoutsLabel")}
                icon={<InterestsIcon />}
                columns={[
                  {
                    id: "program_id",
                    title: I18n.t("Program.titleSingular"),
                    inputComponent: Autocomplete,
                    inputProps: {
                      options: programs,
                      colorAttribute: "color",
                    },
                    headerProps: { width: "50%" },
                  },
                  {
                    id: "seating_layout_ids",
                    inputComponent: Autocomplete,
                    inputProps: {
                      multiple: true,
                      options: formOptions.layouts,
                    },
                  },
                ]}
              />
            </Field>
            <Field fullWidth>
              <List<GroupEventDay>
                id="days"
                label={I18n.t("Event.groupCreate.daysLabel")}
                icon={<LayersIcon />}
                columns={[
                  {
                    id: "date",
                    title: I18n.t("Event.groupCreate.dateColumn"),
                    inputComponent: DatePicker,
                    headerProps: { style: { minWidth: 140 } },
                  },
                  {
                    id: "start_times",
                    title: I18n.t("Event.groupCreate.startTimesColumn"),
                    inputComponent: Autocomplete,
                    inputProps: {
                      multiple: true,
                      options: times,
                      freeSolo: true,
                    },
                  },
                  {
                    id: "_copy" as any,
                    title: "",
                    inputComponent: CopyTimesButton,
                    headerProps: { style: { width: 40 } },
                  },
                ]}
              />
            </Field>
          </FieldsContainer>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {I18n.t("App.cancel")}
        </Button>
        <Button onClick={() => save()} loading={saving} variant="contained">
          {I18n.t("Event.groupCreate.createButton")}
        </Button>
      </DialogActions>
    </>
  );
}
